import getEnvMode from '../../../utils/env';
import { ensureSafariAudioSession } from '../../../utils/platform';

export default class Recorder {
  private mimeType: string;

  private mediaRecorder: MediaRecorder;

  private audioChunks: Blob[] = [];

  private audioContext: AudioContext;

  private analyser: AnalyserNode;

  private dataArray: Float32Array<ArrayBuffer>;

  private recordingStartTime: number;

  private starting: boolean = false;

  private stopped: boolean = false;

  constructor(mimeType = 'audio/webm') {
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      console.error(`MIME type ${mimeType} is not supported, falling back to audio/webm`);
      this.mimeType = 'audio/webm';
    }
    this.mimeType = mimeType;
  }

  async startRecording(deviceId?: string) {
    ensureSafariAudioSession();

    if (this.isRecording() || this.starting) {
      return;
    }
    this.starting = true;
    this.stopped = false;

    const constraints = {
      audio: {
        deviceId: deviceId ? { exact: deviceId } : undefined,
        echoCancellation: false,
        noiseSuppression: false,
      },
    };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    this.audioContext = new AudioContext();
    const source = this.audioContext.createMediaStreamSource(stream);

    this.analyser = this.audioContext.createAnalyser();
    source.connect(this.analyser);
    this.analyser.fftSize = 2048;
    const bufferLength = this.analyser.frequencyBinCount;
    this.dataArray = new Float32Array(bufferLength);

    this.mediaRecorder = new MediaRecorder(stream, { mimeType: this.mimeType });
    this.audioChunks = [];
    this.mediaRecorder.ondataavailable = (event) => {
      this.audioChunks.push(event.data);
    };

    this.mediaRecorder.onstart = () => {
      this.recordingStartTime = performance.now();
      this.starting = false;
    };

    this.mediaRecorder.start();

    // debug info: print the microphone being used
    if (getEnvMode() === 'development') {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputDevices = devices.filter((device) => device.kind === 'audioinput');
      const track = stream.getAudioTracks()[0];
      const device = audioInputDevices.find((d) => d.deviceId === track.getSettings().deviceId);
      if (device) {
        console.log(`Using microphone: ${device.label}`);
      }
    }
  }

  // NOTE: getRecording can only be called after promise resolves!
  stopRecording(): Promise<void> {
    if (
      !this.mediaRecorder
      || this.starting
      || this.stopped
      || this.mediaRecorder.state === 'inactive'
    ) {
      return Promise.resolve();
    }
    this.stopped = true;
    return new Promise((resolve) => {
      const finalize = () => {
        this.audioContext?.close();
        try {
          this.mediaRecorder?.stream.getTracks().forEach((track) => track.stop());
        } catch (_) {
          /* ignore */
        }
        resolve();
      };
      this.mediaRecorder.onstop = finalize;
      try {
        this.mediaRecorder.stop();
      } catch (_) {
        finalize();
      }
    });
  }

  isRecording(): boolean {
    return this.mediaRecorder?.state === 'recording';
  }

  getWaveformValue(): number {
    if (!this.analyser) {
      return 0;
    }
    this.analyser.getFloatTimeDomainData(this.dataArray);
    const maxVal = this.dataArray.reduce((acc, val) => Math.max(acc, val), 0);
    return maxVal;
  }

  getCurrentRecordingLength(): number {
    if (this.audioContext) {
      return this.audioContext.currentTime;
    }
    return 0;
  }

  getRecording(): Blob {
    return new Blob(this.audioChunks, { type: this.mimeType });
  }

  getRecordingStartTime(): number {
    return this.recordingStartTime;
  }
}
